import { Link } from 'react-router-dom';

const costLayers = [
  {
    name: 'Spread',
    when: 'The moment you enter',
    width: '78%',
    dotClass: 'bg-primary-500',
    barClass: 'bg-primary-500/50',
    gradientFrom: 'from-primary-500/25',
    gradientTo: 'to-primary-500/0',
  },
  {
    name: 'Commission',
    when: 'On open & close',
    width: '52%',
    dotClass: 'bg-accent-500',
    barClass: 'bg-accent-500/50',
    gradientFrom: 'from-accent-500/25',
    gradientTo: 'to-accent-500/0',
  },
  {
    name: 'Swap',
    when: 'If held overnight',
    width: '62%',
    dotClass: 'bg-secondary-500',
    barClass: 'bg-secondary-500/50',
    gradientFrom: 'from-secondary-500/25',
    gradientTo: 'to-secondary-500/0',
  },
  {
    name: 'Slippage',
    when: 'During fast markets',
    width: '42%',
    dotClass: 'bg-primary-400',
    barClass: 'bg-primary-400/45',
    gradientFrom: 'from-primary-400/20',
    gradientTo: 'to-primary-400/0',
  },
  {
    name: 'Conversion',
    when: 'Cross-currency pairs',
    width: '30%',
    dotClass: 'bg-foreground-400',
    barClass: 'bg-foreground-400/45',
    gradientFrom: 'from-foreground-400/20',
    gradientTo: 'to-foreground-400/0',
  },
];

export default function HeroSection() {
  return (
    <section className="relative w-full min-h-[520px] md:min-h-[720px] flex items-center overflow-hidden bg-foreground-950">

      {/* ── Decorative: ultra-subtle dot grid ── */}
      <div
        className="absolute inset-0 opacity-[0.015]"
        style={{
          backgroundImage: 'radial-gradient(circle, oklch(var(--accent-500)) 1px, transparent 1px)',
          backgroundSize: '36px 36px',
        }}
      />

      {/* ── Decorative: three very faint ring groups (desktop only) ── */}
      <div className="hidden md:block absolute -top-[200px] -right-[120px] w-[580px] h-[580px] rounded-full border border-accent-500/[0.04]" />
      <div className="hidden md:block absolute -top-[140px] -right-[60px] w-[460px] h-[460px] rounded-full border border-accent-500/[0.03]" />

      <div className="hidden md:block absolute -bottom-[180px] -left-[100px] w-[540px] h-[540px] rounded-full border border-primary-500/[0.03]" />
      <div className="hidden md:block absolute -bottom-[130px] -left-[50px] w-[440px] h-[440px] rounded-full border border-primary-500/[0.02]" />

      <div className="hidden md:block absolute top-[50%] -right-[80px] -translate-y-1/2 w-[420px] h-[420px] rounded-full border border-secondary-500/[0.025]" />

      {/* Mobile: single faint ring */}
      <div className="md:hidden absolute -top-[100px] -right-[60px] w-[260px] h-[260px] rounded-full border border-accent-500/[0.04]" />
      <div className="md:hidden absolute -bottom-[100px] -left-[60px] w-[240px] h-[240px] rounded-full border border-primary-500/[0.03]" />

      {/* ── Decorative: two faint diagonal lines (desktop only) ── */}
      <div className="hidden md:block absolute top-[140px] left-[8%] w-[120px] h-[1px] bg-gradient-to-r from-accent-500/0 via-accent-500/15 to-accent-500/0 rotate-[12deg]" />
      <div className="hidden md:block absolute bottom-[180px] right-[8%] w-[100px] h-[1px] bg-gradient-to-r from-secondary-500/0 via-secondary-500/14 to-secondary-500/0 -rotate-[10deg]" />

      {/* ── Decorative: faint vertical bars (desktop only) ── */}
      <div className="hidden md:flex absolute top-[22%] left-[3%] gap-3">
        <div className="w-[1px] h-14 bg-accent-500/7" />
        <div className="w-[1px] h-20 bg-accent-500/10" />
      </div>
      <div className="hidden md:flex absolute bottom-[25%] right-[4%] gap-3">
        <div className="w-[1px] h-16 bg-primary-500/7" />
        <div className="w-[1px] h-10 bg-primary-500/5" />
      </div>

      {/* ── Decorative: tiny scattered dots (desktop only) ── */}
      <div className="hidden md:flex absolute top-[30%] right-[16%] gap-1.5">
        <span className="w-1 h-1 rounded-full bg-secondary-500/12" />
        <span className="w-1 h-1 rounded-full bg-secondary-500/10" />
      </div>
      <div className="hidden md:flex absolute top-[55%] left-[12%] gap-1.5">
        <span className="w-1 h-1 rounded-full bg-primary-500/10" />
        <span className="w-1 h-1 rounded-full bg-primary-500/12" />
      </div>

      {/* ── Decorative: large soft central glow ── */}
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[300px] md:w-[700px] h-[300px] md:h-[500px] rounded-full opacity-[0.04] blur-[80px] md:blur-[150px] bg-accent-500" />

      {/* ═══════════ Main Content ═══════════ */}
      <div className="relative z-10 w-full max-w-main mx-auto px-4 md:px-6 py-16 md:py-28">
        <div className="flex flex-col lg:flex-row items-center lg:items-start gap-10 md:gap-14 lg:gap-20">

          {/* ── Left: Text ── */}
          <div className="flex-1 lg:max-w-[560px] text-center lg:text-left">
            {/* Heading */}
            <h1 className="text-[34px] md:text-[56px] font-[750] text-background-50 leading-[1.08] tracking-[-0.02em] mb-4 md:mb-5">
              Understand the{' '}
              <span className="relative inline-block">
                <span className="relative z-10">Real Cost</span>
                <span className="absolute left-0 right-0 bottom-[3px] h-[7px] rounded-full bg-accent-500/60" />
              </span>
              <br />
              of a Forex Trade
            </h1>

            {/* Description */}
            <p className="text-[15px] md:text-[18px] text-foreground-400 leading-relaxed max-w-[500px] mx-auto lg:mx-0 mb-8 md:mb-10">
              Spread, commission, swap, slippage and hidden charges — broken down clearly before you open or hold a position.
            </p>

            {/* CTA */}
            <div className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-3 mb-6 md:mb-8">
              <Link
                to="/calculate-forex-trading-costs/"
                className="inline-flex items-center justify-center h-[50px] md:h-[54px] px-6 md:px-8 rounded-pill text-background-50 text-[14px] md:text-[15px] font-bold whitespace-nowrap no-underline transition-all hover:scale-[1.03] active:scale-[0.98] bg-primary-600"
                style={{ boxShadow: '0 10px 36px oklch(0.42 0.18 265 / 0.30)' }}
              >
                Start with Cost Calculation
                <i className="ri-arrow-right-line ml-2 text-lg"></i>
              </Link>
              <Link
                to="/forex-trading-costs-by-strategy/"
                className="inline-flex items-center justify-center h-[50px] md:h-[54px] px-6 md:px-8 rounded-pill text-background-50 text-[14px] md:text-[15px] font-bold whitespace-nowrap no-underline transition-all hover:scale-[1.03] active:scale-[0.98] bg-background-50/10 border border-background-50/15 hover:bg-background-50/18"
              >
                Cost by Strategy
                <i className="ri-arrow-right-up-line ml-2 text-lg"></i>
              </Link>
            </div>

            {/* Stats row */}
            <div className="flex items-center justify-center lg:justify-start gap-4 sm:gap-6 md:gap-8 flex-wrap">
              <div className="flex items-center gap-2">
                <span className="text-[22px] md:text-[24px] font-[750] text-background-50 leading-none">5</span>
                <span className="text-[12px] md:text-[13px] text-foreground-500 leading-tight">Cost Types<br />Explained</span>
              </div>
              <div className="w-[1px] h-7 md:h-8 bg-foreground-800/60" />
              <div className="flex items-center gap-2">
                <span className="text-[22px] md:text-[24px] font-[750] text-background-50 leading-none">6</span>
                <span className="text-[12px] md:text-[13px] text-foreground-500 leading-tight">In-Depth<br />Guides</span>
              </div>
              <div className="w-[1px] h-7 md:h-8 bg-foreground-800/60" />
              <div className="flex items-center gap-2">
                <span className="text-[22px] md:text-[24px] font-[750] text-background-50 leading-none">1</span>
                <span className="text-[12px] md:text-[13px] text-foreground-500 leading-tight">Interactive<br />Calculator</span>
              </div>
            </div>

            {/* Disclaimer */}
            <p className="mt-6 md:mt-7 text-[12px] md:text-[13px] text-foreground-600">
              Educational guide only. Broker conditions and trading costs can change.
            </p>
          </div>

          {/* ── Right: Cost Stack Visual ── */}
          <div className="flex-shrink-0 w-full lg:w-[460px]">
            <div className="rounded-[22px] md:rounded-[28px] p-5 md:p-8 bg-foreground-900/85 border border-foreground-800/60 backdrop-blur-md"
            >
              {/* Header */}
              <div className="flex items-center gap-2.5 mb-1">
                <div className="w-2 h-2 rounded-full bg-accent-500" />
                <span className="text-[11px] md:text-[12px] font-semibold uppercase tracking-[0.12em] text-accent-400">
                  Trade Cost Stack
                </span>
              </div>
              <h3 className="text-[16px] md:text-[20px] font-[750] text-background-50 mb-4 md:mb-6 leading-tight">
                What hits your trade — and when
              </h3>

              {/* Cost layers */}
              <div className="flex flex-col gap-2 md:gap-2.5">
                {costLayers.map((layer) => (
                  <div
                    key={layer.name}
                    className="relative group flex items-center gap-3 md:gap-3.5 h-[52px] md:h-[58px] rounded-[12px] md:rounded-[14px] px-3 md:px-4 transition-colors hover:bg-white/[0.04] overflow-hidden"
                  >
                    {/* Subtle gradient overlay on hover */}
                    <div className={`absolute inset-0 bg-gradient-to-r ${layer.gradientFrom} ${layer.gradientTo} opacity-0 group-hover:opacity-100 transition-opacity duration-300`} />

                    {/* Color dot */}
                    <div
                      className={`relative z-10 w-2.5 h-2.5 rounded-full flex-shrink-0 ${layer.dotClass}`}
                    />

                    {/* Info */}
                    <div className="relative z-10 flex-1 min-w-0">
                      <div className="flex items-baseline gap-2 md:gap-2.5">
                        <span className="text-[14px] md:text-[15px] font-semibold text-white/90 whitespace-nowrap">
                          {layer.name}
                        </span>
                        <span className="text-[11px] md:text-[12px] text-foreground-500 whitespace-nowrap">
                          {layer.when}
                        </span>
                      </div>
                      {/* Impact bar */}
                      <div className="mt-1.5 md:mt-2 h-[3px] rounded-full bg-white/[0.08] overflow-hidden">
                        <div
                          className={`h-full rounded-full ${layer.barClass}`}
                          style={{ width: layer.width }}
                        />
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              {/* Bottom note */}
              <div className="mt-4 md:mt-5 pt-4 md:pt-5 border-t border-foreground-800/50">
                <div className="flex items-start gap-2">
                  <div className="w-4 md:w-5 h-4 md:h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-information-line text-foreground-500 text-xs md:text-sm" />
                  </div>
                  <p className="text-[11px] md:text-[12px] leading-relaxed text-foreground-500">
                    Every layer adds up. A typical EUR/USD scalp trade faces 2–3 cost types at once — the spread and commission are unavoidable, but the others depend on your strategy and timing.
                  </p>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}